import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Eye, EyeOff, Fingerprint, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateOfBirthPicker } from "@/components/DateOfBirthPicker";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, token, user } = useAuth();
  const [knownUser, setKnownUser] = useState(typeof window !== "undefined" ? localStorage.getItem("omni_known_user") : null);
  const [isRegister, setIsRegister] = useState(!knownUser);
  const [username, setUsername] = useState(knownUser || "");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [dob, setDob] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [pendingParental, setPendingParental] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const webauthnSupported = typeof window !== "undefined" && browserSupportsWebAuthn();

  // Only redirect if both token AND user are confirmed valid
  if (token && user) return <Navigate to="/chat" replace />;

  const computeAge = (d) => {
    if (!d) return null;
    const b = new Date(d);
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
    return a;
  };
  const regAge = computeAge(dob);
  const needsParent = regAge !== null && regAge >= 13 && regAge <= 15;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "auth/register" : "auth/login";
      const refCode = new URLSearchParams(window.location.search).get("ref");
      const payload = isRegister
        ? { username, password, email: email || undefined, age_confirmed: consentChecked, consent: consentChecked, date_of_birth: dob, parent_email: parentEmail || undefined, referral_code: refCode || undefined }
        : { username, password };

      const { data } = await axios.post(`${API}/${endpoint}`, payload);
      if (data.status === "parental_consent_required") {
        setPendingParental(true);
        return;
      }
      login(data.token, data.user);
      toast.success(isRegister ? "Welcome to Omni!" : "Welcome back!");
      navigate(data.user.onboarding_completed ? "/chat" : "/onboarding");
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg) {
        toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
        if (isRegister && typeof msg === "string" && /already/i.test(msg)) {
          setIsRegister(false);
          toast.info("Looks like you already have an account — just log in below to access your history.");
        }
      } else if (!err.response) {
        toast.error("Unable to reach the server. Check your connection.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const { data: optionsJSON } = await axios.post(`${API}/auth/webauthn/login/options`, {});
      const sessionId = optionsJSON.sessionId;
      delete optionsJSON.sessionId;
      const authResp = await startAuthentication({ optionsJSON });
      authResp.sessionId = sessionId;
      const { data } = await axios.post(`${API}/auth/webauthn/login/verify`, authResp);
      login(data.token, data.user);
      toast.success("Welcome back!");
      navigate(data.user.onboarding_completed ? "/chat" : "/onboarding");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Biometric login failed";
      if (msg.includes("not recognized") || msg.includes("not found")) {
        toast.error("No biometric credentials found. Log in with password first, then enable biometrics in Settings.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-12">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-2xl font-medium">Omni</span>
        </Link>

        {pendingParental ? (
          <div className="card-omni p-8 md:p-10 text-center" data-testid="parental-pending-card">
            <h1 className="font-heading text-2xl font-medium mb-3">Almost there!</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Because you're aged 13&ndash;15, we've emailed your parent or guardian a consent link.
              Your account will be activated as soon as they approve it &mdash; then you can log in here.
            </p>
            <Button onClick={() => { setPendingParental(false); setIsRegister(false); }} variant="outline" className="rounded-full" data-testid="parental-pending-back-btn">
              Back to Log In
            </Button>
          </div>
        ) : (
        <div className="card-omni p-8 md:p-10">
          <h1 className="font-heading text-2xl font-medium text-center mb-2">
            {isRegister ? "Create your space" : "Welcome back"}
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8" data-testid="auth-subtitle">
            {isRegister
              ? "Just a username and password. No email needed."
              : knownUser
                ? `Welcome back, ${knownUser}! Log in to pick up right where you left off.`
                : "Your companion is waiting."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-2 block">{isRegister ? "Username" : "Username or Email"}</label>
              <input
                data-testid="auth-username-input"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="input-omni w-full border border-input"
                placeholder={isRegister ? "Choose a username" : "Username or email address"}
                required
                minLength={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Password</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input-omni w-full border border-input pr-12"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Email <span className="text-muted-foreground font-normal">(optional, for recovery)</span>
                </label>
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-omni w-full border border-input"
                  placeholder="your@email.com"
                />
              </div>
            )}

            {isRegister && (
              <div>
                <label className="text-sm font-medium mb-2 block">Date of Birth</label>
                <DateOfBirthPicker value={dob} onChange={setDob} age={regAge} />
                {regAge !== null && regAge < 13 && (
                  <p className="text-xs text-destructive mt-1.5" data-testid="underage-message">
                    Omni is available to users aged 13 and older.
                  </p>
                )}
              </div>
            )}

            {isRegister && needsParent && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Parent / Guardian Email <span className="text-muted-foreground font-normal">(required for ages 13–15)</span>
                </label>
                <input
                  data-testid="auth-parent-email-input"
                  type="email"
                  value={parentEmail}
                  onChange={e => setParentEmail(e.target.value)}
                  className="input-omni w-full border border-input"
                  placeholder="parent@email.com"
                  required
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  We'll email your parent or guardian a consent link. Your account activates once they approve.
                </p>
              </div>
            )}

            {isRegister && (
              <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer" data-testid="consent-checkbox-label">
                <input
                  data-testid="consent-checkbox"
                  type="checkbox"
                  checked={consentChecked}
                  onChange={e => setConsentChecked(e.target.checked)}
                  required
                  className="mt-0.5 accent-primary w-4 h-4 shrink-0"
                />
                <span>
                  I confirm I am <strong>13 years or older</strong> and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-primary underline">Terms of Service</Link> and{" "}
                  <Link to="/privacy" target="_blank" className="text-primary underline">Privacy Policy</Link>.
                </span>
              </label>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading || (isRegister && (!consentChecked || !dob || (regAge !== null && regAge < 13) || (needsParent && !parentEmail)))}
              className="w-full btn-omni bg-primary text-primary-foreground h-14 text-base"
            >
              {loading ? "..." : isRegister ? "Create Account" : "Log In"}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {webauthnSupported && !isRegister && (
              <div className="mb-3">
                <div className="relative flex items-center justify-center mb-3">
                  <div className="border-t border-border/50 flex-grow" />
                  <span className="px-3 text-[11px] text-muted-foreground">or</span>
                  <div className="border-t border-border/50 flex-grow" />
                </div>
                <Button
                  data-testid="biometric-login-btn"
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={biometricLoading}
                  variant="outline"
                  className="w-full h-12 rounded-2xl gap-2 text-sm font-medium border-2 hover:border-primary/40 transition-all"
                >
                  {biometricLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Fingerprint className="w-5 h-5 text-primary" />
                  )}
                  {biometricLoading ? "Verifying..." : "Sign in with Biometrics"}
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1.5">Face ID, Touch ID, or Windows Hello</p>
              </div>
            )}
            {knownUser ? (
              <button
                data-testid="auth-switch-account-btn"
                type="button"
                onClick={() => {
                  localStorage.removeItem("omni_known_user");
                  setKnownUser(null);
                  setIsRegister(true);
                  setUsername("");
                }}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Not {knownUser}? Sign in with a different account
              </button>
            ) : (
              <button
                data-testid="auth-toggle-btn"
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isRegister ? "Already have an account? Log in" : "Need an account? Register"}
              </button>
            )}
            {!isRegister && (
              <div>
                <Link
                  to="/reset-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
          </div>
        </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-6 max-w-xs mx-auto">
          Omni is an AI companion, not a licensed professional.
          In a crisis, please contact emergency services.
        </p>
      </div>
    </div>
  );
}
